import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDepartmentConsumptionReport,
  exportDepartmentConsumptionReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchAllInventorySubcategories, fetchDepartmentDropdown } from 'store/slices/commonSlice';
import { fetchDepartments } from 'store/slices/departmentSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Building2, Package, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import FormField from 'components/common/FormField';

const DepartmentConsumptionPage = () => {
  const dispatch = useDispatch();

  const {
    departmentConsumptionReport = {},
    isLoading,
    error,
  } = useSelector((state) => state.report);

  // Get all subcategories from Redux state (not filtered by category)
  const subcategories = useSelector((state) => state.common?.allInventorySubcategories.items || []);

  
  // Get all departments from Redux state
  const departments = useSelector((state) => state.common?.departments.items || []);

  console
  
  const summary = departmentConsumptionReport?.data?.summary || {};
  const results = departmentConsumptionReport?.data?.results || [];
  const pagination = departmentConsumptionReport?.pagination || {};

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);
  
  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    department: 'all',
    sub_category: 'all',
    total_consumed_gte: '',
    total_consumed_lte: '',
  });

  // Calculate total pages from server response
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value => 
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'department_consumption',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.department !== 'all' && { department: advancedFilters.department }),
      ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
      ...(advancedFilters.total_consumed_gte && { total_consumed__gte: advancedFilters.total_consumed_gte }),
      ...(advancedFilters.total_consumed_lte && { total_consumed__lte: advancedFilters.total_consumed_lte }),
    };

    
    dispatch(fetchDepartmentConsumptionReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch all subcategories and departments on component mount
  useEffect(() => {
    dispatch(fetchAllInventorySubcategories()); // Empty string to get all subcategories
    dispatch(fetchDepartmentDropdown()); // Fetch all departments
  }, [dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      department: 'all',
      sub_category: 'all',
      total_consumed_gte: '',
      total_consumed_lte: '',
    });
  };

  const columns = [
    { accessorKey: 'Department', header: 'Department' },
    { accessorKey: 'Ingredient', header: 'Ingredient / Sub Category' },
    {
      accessorKey: 'Total Consumed',
      header: 'Total Consumed',
      cell: ({ row }) => (
        <span className="font-semibold text-primary">
          {row.original['Total Consumed'] ?? 0}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Department Consumption Report"
        description="Track ingredient consumption across different departments"
      />

      {/* <LogDateFilter onFilter={(f) => setDateFilters(f)} /> */}

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Select
                  value={advancedFilters.department}
                  onValueChange={(value) => handleAdvancedFilterChange('department', value)}
                  // disabled={departmentsLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(departments) && departments.map((department) => (
                      <SelectItem key={department.id} value={department.id}>
                        {department.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="sub_category">Sub Category</Label>
                <Select
                  value={advancedFilters.sub_category}
                  onValueChange={(value) => handleAdvancedFilterChange('sub_category', value)}
                 
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select subcategory"/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(subcategories) && subcategories.map((subcategory) => (
                      <SelectItem key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              <FormField
  label="Department"
  name="department"
  type="select"
  value={advancedFilters.department}
  onChange={(e) =>
    handleAdvancedFilterChange('department', e.target.value)
  }
  options={[
    { value: 'all', label: 'All' },
    ...(Array.isArray(departments)
      ? departments.map((department) => ({
          value: String(department.id),
          label: department.name,
        }))
      : []),
  ]}
/>

<FormField
  label="Sub Category"
  name="sub_category"
  type="select"
  value={advancedFilters.sub_category}
  onChange={(e) =>
    handleAdvancedFilterChange('sub_category', e.target.value)
  }
  options={[
    { value: 'all', label: 'All' },
    ...(Array.isArray(subcategories)
      ? subcategories.map((subcategory) => ({
          value: String(subcategory.id),
          label: subcategory.name,
        }))
      : []),
  ]}
/>

              
              <div className="space-y-2">
                <Label>Total Consumed Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.total_consumed_gte}
                    onChange={(e) => handleAdvancedFilterChange('total_consumed_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.total_consumed_lte}
                    onChange={(e) => handleAdvancedFilterChange('total_consumed_lte', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Departments</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary['Total Departments'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Ingredients Consumed</CardTitle>
            <Package className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {summary['Total Ingredients Consumed'] ?? 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading department consumption data...</span>
        </div>
      ) : ( */}
        <DataTable
          data={results} // Use the paginated results from the server
          columns={columns}
          totalCount={pagination.count}
          searchPlaceholder="Search by department or ingredient..."
          onSearch={setSearchTerm}
          showSearch={true}
          pageIndex={page - 1} // Convert to 0-based index for DataTable
          pageSize={pageSize}
          pageCount={totalPages} // Use total pages from server response
          onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          isLoading={isLoading}
          onExport={() =>
            dispatch(exportDepartmentConsumptionReport({ 
              filters: { 
                page,
                page_size: pageSize,
                search: searchTerm,
                report_type: 'department_consumption',
                ...dateFilters,
                // Include advanced filters in export
                ...(advancedFilters.department !== 'all' && { department: advancedFilters.department }),
                ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
                ...(advancedFilters.total_consumed_gte && { total_consumed__gte: advancedFilters.total_consumed_gte }),
                ...(advancedFilters.total_consumed_lte && { total_consumed__lte: advancedFilters.total_consumed_lte }),
              } 
            }))
          }
          showExport={true}
          showImport={false}
        />
      {/* )} */}
    </div>
  );
};

export default DepartmentConsumptionPage;